
import { useState } from "react";
import { Product, Group, UsageRecord } from "@/lib/types";
import { generateId, addUsageRecord } from "@/lib/storage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import UsageFormHeader from "./UsageFormHeader";
import UsageDatePicker from "./UsageDatePicker";
import UsageProductList from "./UsageProductList";

interface UsageFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: Product[];
  product?: Product;
  group?: Group;
  onSuccess: () => void;
}

const UsageForm = ({ 
  open, 
  onOpenChange, 
  product,
  group,
  products,
  onSuccess
}: UsageFormProps) => {
  const [date, setDate] = useState<Date>(new Date());
  const [selectedProducts, setSelectedProducts] = useState<{id: string, quantity: number}[]>([]);
  
  // Set up initial selected products based on either single product or group
  useState(() => {
    if (product) {
      setSelectedProducts([{ id: product.id, quantity: 1 }]);
    } else if (group) {
      const groupProducts = products.filter(p => group.productIds.includes(p.id));
      setSelectedProducts(
        groupProducts.map(p => ({ id: p.id, quantity: 1 }))
      );
    } else {
      setSelectedProducts([]);
    }
  });
  
  const handleQuantityChange = (id: string, quantity: number) => {
    setSelectedProducts(prev => 
      prev.map(item => 
        item.id === id ? { ...item, quantity } : item
      )
    );
  };
  
  const handleToggleProduct = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, { id, quantity: 1 }]);
    } else {
      setSelectedProducts(prev => prev.filter(item => item.id !== id));
    }
  };
  
  const handleSubmit = () => {
    // Create usage records for each selected product
    selectedProducts.forEach(item => {
      if (item.quantity > 0) {
        const record: UsageRecord = {
          id: generateId(),
          productId: item.id,
          quantity: item.quantity,
          date,
        };
        
        addUsageRecord(record);
      }
    });
    
    onSuccess();
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] glass-card animate-fade-in">
        <UsageFormHeader product={product} group={group} />
        <div className="space-y-4 py-4">
          <UsageDatePicker date={date} setDate={setDate} />
          
          <UsageProductList
            product={product}
            group={group}
            products={products}
            selectedProducts={selectedProducts}
            handleQuantityChange={handleQuantityChange}
            handleToggleProduct={handleToggleProduct}
          />
          
          {selectedProducts.length > 0 && (
            <div className="pt-2">
              <p className="text-sm font-medium">
                Recording usage for {selectedProducts.length} {selectedProducts.length === 1 ? 'product' : 'products'}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={selectedProducts.length === 0}
            className="btn-primary"
          >
            Record Usage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UsageForm;
